// LandingPageV2 — Lean 7-section funnel прототип за B2C пациенти.
// Цел: ясна целева аудитория, един CTA, кратка и фокусирана страница.
// Запазени са брандовите цветове (purple→pink, nature green) и dark/light темите.

import React from "react";
import { Link } from "react-router-dom";
import {
  Camera,
  Brain,
  Activity,
  FlaskConical,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Star,
  Moon,
  Sun,
  TrendingDown,
  AlertCircle,
  Clock,
  Utensils,
  Scan,
  HeartPulse,
  Quote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/SubpageLayout";
import { DR_INKOV_PHOTO } from "@/data/consultants";
import SEO from "@/components/SEO";
import "@/styles/landing-brand.css";

// ─────────────────────────────────────────────────────────────────────
// 1) Compact Navigation
// ─────────────────────────────────────────────────────────────────────
function NavV2({ isDark, toggleTheme, user }) {
  const { pickLang } = useLanguage();
  const nav = {
    why: pickLang("Защо", "Why", "Por qué"),
    how: pickLang("Как работи", "How it works", "Cómo funciona"),
    features: pickLang("Функции", "Features", "Funciones"),
    consultants: pickLang("Консултанти", "Consultants", "Consultores"),
    pricing: pickLang("Цени", "Pricing", "Precios"),
    doctors: pickLang("За лекари", "For doctors", "Para médicos"),
    themeAria: pickLang("Смени темата", "Toggle theme", "Cambiar tema"),
    dashboard: pickLang("Моят профил", "My account", "Mi cuenta"),
    signin: pickLang("Вход", "Sign in", "Entrar"),
    signup: pickLang("Безплатно", "Free", "Gratis"),
  };
  return (
    <nav
      data-testid="landing-v2-nav"
      className={`sticky top-0 z-40 backdrop-blur-xl border-b ${
        isDark
          ? "bg-[hsl(223,47%,6%)]/90 border-[hsl(223,47%,12%)]"
          : "bg-white/90 border-slate-100"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-v2-brand">
          <img src="/app-icon.png" alt="Nutramilo" className="h-8 w-8 object-contain sm:hidden" />
          <img
            src="/nutramilo-logo.png"
            alt="Nutramilo"
            className="hidden sm:block h-8 w-auto object-contain"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </Link>
        <div className="hidden md:flex items-center gap-6">
          <a
            href="#problem"
            data-testid="landing-v2-nav-problem"
            className={`text-sm font-medium ${
              isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"
            }`}
          >
            {nav.why}
          </a>
          <a
            href="#how"
            data-testid="landing-v2-nav-how"
            className={`text-sm font-medium ${
              isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"
            }`}
          >
            {nav.how}
          </a>
          <a
            href="#features"
            data-testid="landing-v2-nav-features"
            className={`text-sm font-medium ${
              isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"
            }`}
          >
            {nav.features}
          </a>
          <Link
            to="/consultants"
            data-testid="landing-v2-nav-consultants"
            className={`text-sm font-medium ${
              isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"
            }`}
          >
            {nav.consultants}
          </Link>
          <Link
            to="/pricing"
            data-testid="landing-v2-nav-pricing"
            className={`text-sm font-medium ${
              isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"
            }`}
          >
            {nav.pricing}
          </Link>
          <Link
            to="/for-doctors"
            data-testid="landing-v2-nav-doctors"
            className={`text-sm font-medium ${
              isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"
            }`}
          >
            {nav.doctors}
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSwitcher isDark={isDark} testIdPrefix="landing-v2-lang" />
          <button
            onClick={toggleTheme}
            data-testid="landing-v2-theme-toggle"
            className={`p-2 rounded-lg ${
              isDark
                ? "hover:bg-white/5 text-slate-300"
                : "hover:bg-slate-100 text-slate-600"
            }`}
            aria-label={nav.themeAria}
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {user ? (
            <Link to="/dashboard">
              <Button
                size="sm"
                data-testid="landing-v2-nav-dashboard"
                className="brand-cta-primary rounded-full px-5 text-xs font-semibold"
              >
                {nav.dashboard}
              </Button>
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="landing-v2-nav-signin"
                className={`hidden sm:inline-flex text-xs font-semibold px-3 py-1.5 rounded-full ${
                  isDark
                    ? "text-slate-300 hover:text-white hover:bg-white/5"
                    : "text-slate-700 hover:text-[#287a2e] hover:bg-slate-100"
                }`}
              >
                {nav.signin}
              </Link>
              <Link to="/register">
                <Button
                  size="sm"
                  data-testid="landing-v2-nav-signup"
                  className="brand-cta-primary rounded-full px-5 text-xs font-semibold"
                >
                  {nav.signup}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 2) Hero — ясна value prop + primary CTA
// ─────────────────────────────────────────────────────────────────────
function Hero({ isDark }) {
  const { pickLang } = useLanguage();
  const t = {
    titleBefore: pickLang("Разберете какво", "Understand what", "Comprenda qué hace"),
    titleAccent: pickLang("всяко хранене", "every meal", "cada comida"),
    titleAfter: pickLang("прави с тялото Ви.", "does to your body.", "en su cuerpo."),
    sub1: pickLang("Снимайте ястие или качете кръвни изследвания — получавате персонализирана обратна връзка през", "Snap a meal or upload lab results — get personal feedback across", "Haga una foto a su plato o cargue sus análisis — reciba una respuesta personalizada a través de"),
    sub2: pickLang("11-те физиологични системи", "the 11 physiological systems", "los 11 sistemas fisiológicos"),
    sub3: pickLang("на тялото Ви. С инсулинов товар и инсулинов индекс, не само калории.", "of your body. With insulin load and insulin index, not just calories.", "de su cuerpo. Con carga e índice insulínicos, no solo calorías."),
    ctaPrimary: pickLang("Започнете безплатно", "Start free", "Empezar gratis"),
    ctaSecondary: pickLang("Как работи", "How it works", "Cómo funciona"),
    users: pickLang("2 400+ потребители", "2,400+ users", "2.400+ usuarios"),
    mealAlt: pickLang("Ястие — боул с киноа, авокадо и зеленчуци", "Meal — bowl with quinoa, avocado and vegetables", "Plato — bol con quinoa, aguacate y verduras"),
    mealTag: pickLang("Анализ за 3 сек.", "3-sec analysis", "Análisis en 3 s"),
    rows: {
      insLoad: pickLang("Инсулинов товар", "Insulin load", "Carga insulínica"),
      insIndex: pickLang("Инсулинов индекс", "Insulin index", "Índice insulínico"),
      glyLoad: pickLang("Гликемичен товар", "Glycemic load", "Carga glucémica"),
      protein: pickLang("Белтъчини", "Protein", "Proteínas"),
      moderate: pickLang("Умерен", "Moderate", "Moderada"),
      low: pickLang("Нисък", "Low", "Baja"),
      high: pickLang("Висок", "High", "Alta"),
    },
    systemsTitle: pickLang("Ефект върху физиологичните системи", "Effect on physiological systems", "Efecto sobre los sistemas fisiológicos"),
    sysEndo: pickLang("Ендокринна", "Endocrine", "Endocrino"),
    sysEndoImpact: pickLang("Стабилен инсулинов отговор", "Stable insulin response", "Respuesta insulínica estable"),
    sysCardio: pickLang("Сърдечно-съдова", "Cardiovascular", "Cardiovascular"),
    sysCardioImpact: pickLang("Нисък възпалителен риск", "Low inflammatory risk", "Riesgo inflamatorio bajo"),
    sysNervous: pickLang("Нервна", "Nervous", "Nervioso"),
    sysNervousImpact: pickLang("Поддържа когнитивна яснота", "Supports cognitive clarity", "Favorece la claridad cognitiva"),
  };
  return (
    <section
      data-testid="landing-v2-hero"
      className={`relative overflow-hidden ${
        isDark ? "bg-[hsl(223,47%,7%)]" : "bg-[#F9F9F7]"
      }`}
    >
      {/* Декоративен gradient blob */}
      <div
        className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #e44288 0%, #9c39a1 50%, transparent 70%)",
        }}
      />
      <div
        className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-15 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #68c349 0%, #0e5c60 50%, transparent 70%)",
        }}
      />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-32">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <h1
              className={`text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6 ${
                isDark ? "text-white" : "text-slate-900"
              }`}
              data-testid="landing-v2-hero-title"
            >
              {t.titleBefore}{" "}
              <span
                style={{
                  background:
                    "linear-gradient(135deg, #5e227f 0%, #9c39a1 50%, #e44288 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {t.titleAccent}
              </span>{" "}
              {t.titleAfter}
            </h1>
            <p
              className={`text-lg sm:text-xl leading-relaxed mb-8 max-w-xl ${
                isDark ? "text-slate-300" : "text-slate-600"
              }`}
              data-testid="landing-v2-hero-sub"
            >
              {t.sub1}{" "}
              <strong>{t.sub2}</strong>{" "}
              {t.sub3}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <Link to="/register">
                <Button
                  size="lg"
                  data-testid="landing-v2-hero-cta-primary"
                  className="brand-cta-primary rounded-full px-7 py-6 text-base font-semibold w-full sm:w-auto"
                >
                  {t.ctaPrimary}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#how">
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="landing-v2-hero-cta-secondary"
                  className={`rounded-full px-7 py-6 text-base font-semibold w-full sm:w-auto ${
                    isDark
                      ? "border-white/20 text-white hover:bg-white/5"
                      : "border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {t.ctaSecondary}
                </Button>
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <div className="flex -space-x-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`w-7 h-7 rounded-full border-2 ${
                        isDark ? "border-[hsl(223,47%,7%)]" : "border-white"
                      }`}
                      style={{
                        background: `linear-gradient(135deg, hsl(${
                          i * 60
                        }, 70%, 55%), hsl(${i * 60 + 30}, 70%, 65%))`,
                      }}
                    />
                  ))}
                </div>
                <span
                  className={`font-semibold ml-1 ${
                    isDark ? "text-slate-300" : "text-slate-700"
                  }`}
                >
                  {t.users}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
                <span
                  className={`font-semibold ml-1 ${
                    isDark ? "text-slate-300" : "text-slate-700"
                  }`}
                >
                  4.8 / 5
                </span>
              </div>
            </div>
          </div>

          {/* Hero visual — meal preview + metrics + system impact */}
          <div className="lg:col-span-2">
            <div
              className={`relative rounded-3xl p-5 shadow-2xl ${
                isDark
                  ? "bg-[hsl(223,47%,10%)] border border-white/10"
                  : "bg-white border border-slate-200"
              }`}
              data-testid="landing-v2-hero-visual"
            >
              {/* Meal photo */}
              <div
                className="aspect-[4/3] rounded-2xl overflow-hidden mb-4 relative"
                style={{
                  background:
                    "linear-gradient(135deg, #5e227f 0%, #e44288 100%)",
                }}
              >
                <img
                  src="https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=720&q=70"
                  alt={t.mealAlt}
                  className="w-full h-full object-cover"
                  loading="eager"
                  data-testid="landing-v2-hero-meal-photo"
                />
                <div
                  className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold text-white backdrop-blur-sm bg-black/40"
                  data-testid="landing-v2-hero-meal-tag"
                >
                  <Camera className="w-3 h-3" />
                  {t.mealTag}
                </div>
              </div>

              {/* Metrics */}
              <div
                className={`space-y-2 pb-3 mb-3 border-b ${
                  isDark ? "border-white/10" : "border-slate-100"
                }`}
                data-testid="landing-v2-hero-metrics"
              >
                {[
                  { label: t.rows.insLoad, value: t.rows.moderate, tone: "#287a2e" },
                  { label: t.rows.insIndex, value: "48", tone: "#287a2e" },
                  { label: t.rows.glyLoad, value: t.rows.low, tone: "#287a2e" },
                  { label: t.rows.protein, value: t.rows.high, tone: "#287a2e" },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center justify-between py-1"
                  >
                    <span
                      className={`text-xs ${
                        isDark ? "text-slate-400" : "text-slate-600"
                      }`}
                    >
                      {row.label}
                    </span>
                    <span
                      className="text-xs font-bold"
                      style={{ color: row.tone }}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* System impact feedback */}
              <div data-testid="landing-v2-hero-systems">
                <div
                  className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${
                    isDark ? "text-slate-400" : "text-slate-500"
                  }`}
                >
                  {t.systemsTitle}
                </div>
                {[
                  {
                    icon: HeartPulse,
                    sys: t.sysEndo,
                    impact: t.sysEndoImpact,
                    tone: "#287a2e",
                  },
                  {
                    icon: Activity,
                    sys: t.sysCardio,
                    impact: t.sysCardioImpact,
                    tone: "#287a2e",
                  },
                  {
                    icon: Brain,
                    sys: t.sysNervous,
                    impact: t.sysNervousImpact,
                    tone: "#287a2e",
                  },
                ].map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div
                      key={i}
                      data-testid={`landing-v2-hero-system-${i}`}
                      className="flex items-start gap-2 py-1.5"
                    >
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(40,122,46,0.15), rgba(104,195,73,0.15))",
                        }}
                      >
                        <Icon className="w-3.5 h-3.5" style={{ color: s.tone }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-xs font-semibold ${
                            isDark ? "text-slate-200" : "text-slate-800"
                          }`}
                        >
                          {s.sys}
                        </div>
                        <div
                          className={`text-[11px] leading-snug ${
                            isDark ? "text-slate-400" : "text-slate-500"
                          }`}
                        >
                          {s.impact}
                        </div>
                      </div>
                      <CheckCircle2
                        className="w-4 h-4 flex-shrink-0 mt-1"
                        style={{ color: s.tone }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Video Showcase — демонстрация на платформата
// ─────────────────────────────────────────────────────────────────────
function VideoShowcase({ isDark }) {
  const { pickLang } = useLanguage();
  const t = {
    eyebrow: pickLang("Как изглежда на практика", "How it looks in practice", "Cómo se ve en la práctica"),
    title: pickLang("Вашето здраве. Свързано.", "Your health. Connected.", "Su salud. Conectada."),
    hint: pickLang("Използвайте контролите на видеото, за да го пуснете със звук.", "Use the video controls to play with sound.", "Use los controles del vídeo para reproducirlo con sonido."),
  };
  const videoRef = React.useRef(null);
  const videoSrc =
    "https://customer-assets-4nw71qhi.emergentagent.net/job_endocrine-hub-2/artifacts/tpakzxre_Nutramilo%20%E2%80%94%20Your%20Health.%20Connected.%20v1.mp4";

  // Autoplay (muted) on scroll into view
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.25) {
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <section
      data-testid="landing-v2-video"
      className={`py-16 sm:py-20 ${
        isDark ? "bg-[hsl(223,47%,8%)]" : "bg-white"
      }`}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div
            className="inline-block text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: "#287a2e" }}
            data-testid="landing-v2-video-eyebrow"
          >
            {t.eyebrow}
          </div>
          <h2
            className={`text-3xl sm:text-4xl font-bold tracking-tight ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            {t.title}
          </h2>
        </div>
        <div
          className={`relative rounded-3xl overflow-hidden shadow-2xl group ${
            isDark
              ? "ring-1 ring-white/10 bg-[hsl(223,47%,10%)]"
              : "ring-1 ring-slate-200"
          }`}
          style={{
            background:
              "linear-gradient(135deg, #0e5c60 0%, #5e227f 50%, #e44288 100%)",
          }}
          data-testid="landing-v2-video-card"
        >
          <video
            ref={videoRef}
            src={videoSrc}
            muted
            loop
            playsInline
            controls
            preload="auto"
            poster="/videos/hero-poster.jpg"
            className="w-full aspect-video object-cover relative z-10"
            data-testid="landing-v2-video-player"
          />
        </div>
        <p
          className={`text-center text-xs mt-4 ${
            isDark ? "text-slate-500" : "text-slate-500"
          }`}
          data-testid="landing-v2-video-hint"
        >
          {t.hint}
        </p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 3) Problem — защо броенето на калории не работи
// ─────────────────────────────────────────────────────────────────────
function Problem({ isDark }) {
  const { pickLang } = useLanguage();
  const T = {
    eyebrow: pickLang("Проблемът", "The problem", "El problema"),
    title: pickLang("Защо традиционното броене на калории не Ви помага", "Why traditional calorie counting doesn't help you", "Por qué el conteo de calorías no le ayuda"),
    lead: pickLang("Тялото Ви не е таблица. То е мрежа от взаимосвързани системи.", "Your body isn't a spreadsheet. It's a network of interconnected systems.", "Su cuerpo no es una hoja de cálculo. Es una red de sistemas interconectados."),
  };
  const problems = [
    {
      icon: TrendingDown,
      title: pickLang("Калориите не казват всичко", "Calories don't tell the whole story", "Las calorías no lo dicen todo"),
      body: pickLang(
        "500 ккал от сладкиш и 500 ккал от риба имат коренно различен ефект върху кръвната Ви захар, възпалението и съня.",
        "500 kcal from a pastry and 500 kcal from fish have very different effects on your blood sugar, inflammation and sleep.",
        "500 kcal de un pastel y 500 kcal de pescado tienen efectos muy distintos sobre su glucemia, inflamación y sueño."
      ),
    },
    {
      icon: AlertCircle,
      title: pickLang("Общите съвети не Ви вършат работа", "Generic advice doesn't work for you", "Los consejos genéricos no le sirven"),
      body: pickLang(
        "„Яжте по-здравословно\" звучи просто, но тялото Ви реагира различно на всяко ястие — според Вашата физиология.",
        "\"Eat healthier\" sounds simple, but your body reacts differently to each meal — according to your own physiology.",
        "\"Coma más sano\" suena simple, pero su cuerpo reacciona de forma distinta a cada comida — según su propia fisiología."
      ),
    },
    {
      icon: Clock,
      title: pickLang("Резултатите идват със закъснение", "Results come too late", "Los resultados llegan tarde"),
      body: pickLang(
        "До следващите кръвни изследвания минават месеци. Nutramilo Ви показва ефекта в реално време.",
        "Months pass before your next lab tests. Nutramilo shows the effect in real time.",
        "Pasan meses hasta sus próximos análisis. Nutramilo le muestra el efecto en tiempo real."
      ),
    },
  ];
  return (
    <section
      id="problem"
      data-testid="landing-v2-problem"
      className={`py-20 sm:py-24 ${isDark ? "bg-[hsl(223,47%,8%)]" : "bg-white"}`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <div
            className="inline-block text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: "#9c39a1" }}
          >
            {T.eyebrow}
          </div>
          <h2
            className={`text-3xl sm:text-4xl font-bold tracking-tight mb-4 ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            {T.title}
          </h2>
          <p
            className={`text-lg ${isDark ? "text-slate-400" : "text-slate-600"}`}
          >
            {T.lead}
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {problems.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                data-testid={`landing-v2-problem-card-${i}`}
                className={`p-7 rounded-2xl ${
                  isDark
                    ? "bg-[hsl(223,47%,10%)] border border-white/10"
                    : "bg-[#F9F9F7] border border-slate-100"
                }`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(228,66,136,0.15), rgba(156,57,161,0.15))",
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: "#9c39a1" }} />
                </div>
                <h3
                  className={`text-lg font-semibold mb-2 ${
                    isDark ? "text-white" : "text-slate-900"
                  }`}
                >
                  {p.title}
                </h3>
                <p
                  className={`text-sm leading-relaxed ${
                    isDark ? "text-slate-400" : "text-slate-600"
                  }`}
                >
                  {p.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 4) Solution — 3-step How It Works
// ─────────────────────────────────────────────────────────────────────
function Solution({ isDark }) {
  const { pickLang } = useLanguage();
  const T = {
    eyebrow: pickLang("Решението", "The solution", "La solución"),
    title: pickLang("Три прости стъпки до по-добро здраве", "Three simple steps to better health", "Tres pasos simples hacia una mejor salud"),
  };
  const steps = [
    {
      n: "01",
      icon: Camera,
      title: pickLang("Снимайте или опишете", "Capture or describe", "Fotografíe o describa"),
      body: pickLang(
        "Направете снимка на ястието, кажете го с глас или го въведете ръчно. Отнема 10 секунди.",
        "Take a photo of your meal, say it out loud or enter it manually. It takes 10 seconds.",
        "Haga una foto a su plato, dígalo en voz alta o introdúzcalo manualmente. Tarda 10 segundos."
      ),
    },
    {
      n: "02",
      icon: Brain,
      title: pickLang("AI анализ през 11 системи", "AI analysis across 11 systems", "Análisis con IA de 11 sistemas"),
      body: pickLang(
        "Нашата мрежа от дигитални близнаци анализира ефекта върху всичките 11 физиологични системи — метаболизъм, ендокринна, сърдечно-съдова, когнитивна и още.",
        "Our digital-twin network analyses the effect on all 11 physiological systems — metabolism, endocrine, cardiovascular, cognitive and more.",
        "Nuestra red de gemelos digitales analiza el efecto sobre los 11 sistemas fisiológicos — metabolismo, endocrino, cardiovascular, cognitivo y más."
      ),
    },
    {
      n: "03",
      icon: Activity,
      title: pickLang("Симулатор на чинията", "Plate simulator", "Simulador del plato"),
      body: pickLang(
        "Вижте предварително инсулиновия и гликемичен товар, преди да хапнете. Симулаторът е свързан с мрежата от дигитални близнаци и показва ефекта в реално време.",
        "See the insulin and glycemic load before you eat. The simulator is connected to the digital-twin network and shows the effect in real time.",
        "Vea la carga insulínica y glucémica antes de comer. El simulador está conectado a la red de gemelos digitales y muestra el efecto en tiempo real."
      ),
    },
  ];
  return (
    <section
      id="how"
      data-testid="landing-v2-solution"
      className={`py-20 sm:py-24 ${
        isDark ? "bg-[hsl(223,47%,7%)]" : "bg-[#F9F9F7]"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div
            className="inline-block text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: "#287a2e" }}
          >
            {T.eyebrow}
          </div>
          <h2
            className={`text-3xl sm:text-4xl font-bold tracking-tight mb-4 ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            {T.title}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 relative">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                data-testid={`landing-v2-step-${i}`}
                className={`relative p-7 rounded-2xl ${
                  isDark
                    ? "bg-[hsl(223,47%,10%)] border border-white/10"
                    : "bg-white border border-slate-100 shadow-sm"
                }`}
              >
                <div
                  className="text-6xl font-black mb-4 opacity-10"
                  style={{ color: "#287a2e" }}
                >
                  {s.n}
                </div>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 -mt-10 relative"
                  style={{
                    background:
                      "linear-gradient(135deg, #0e5c60 0%, #68c349 100%)",
                  }}
                >
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-3 ${
                    isDark ? "text-white" : "text-slate-900"
                  }`}
                >
                  {s.title}
                </h3>
                <p
                  className={`text-sm leading-relaxed ${
                    isDark ? "text-slate-400" : "text-slate-600"
                  }`}
                >
                  {s.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 5) Key Features — 4 core features
// ─────────────────────────────────────────────────────────────────────
function Features({ isDark }) {
  const { pickLang } = useLanguage();
  const T = {
    eyebrow: pickLang("Какво получавате", "What you get", "Lo que recibe"),
    title: pickLang("Шест инструмента, обединени в един профил", "Six tools, united in one account", "Seis herramientas, unidas en una sola cuenta"),
    lead: pickLang(
      "Всеки инструмент работи самостоятелно, но заедно рисуват пълна картина на здравето Ви.",
      "Each tool works on its own, but together they draw a complete picture of your health.",
      "Cada herramienta funciona por sí sola, pero juntas forman una imagen completa de su salud."
    ),
  };
  const features = [
    {
      icon: Camera,
      title: pickLang("Умен хранителен дневник", "Smart food diary", "Diario alimentario inteligente"),
      goal: pickLang("Цел: разберете всяко ястие за секунди", "Goal: understand every meal in seconds", "Objetivo: comprender cada comida en segundos"),
      body: pickLang(
        "Снимка, глас или текст — AI разпознава съставките, порциите и изчислява метаболитния ефект. Край на ръчното въвеждане и напъна да помните всичко.",
        "Photo, voice or text — AI recognises ingredients, portions and calculates the metabolic effect. No more manual entry or trying to remember everything.",
        "Foto, voz o texto — la IA reconoce ingredientes y porciones y calcula el efecto metabólico. Se acabó introducir todo a mano."
      ),
      link: "/platform",
    },
    {
      icon: Utensils,
      title: pickLang("Симулатор на чинията", "Plate simulator", "Simulador del plato"),
      goal: pickLang("Цел: решете преди да ядете", "Goal: decide before you eat", "Objetivo: decida antes de comer"),
      body: pickLang(
        "Проверете инсулиновия товар, инсулиновия индекс и хранителния баланс на ястието ПРЕДИ да го изядете. Свързан с мрежата от дигитални близнаци — виждате ефекта върху всяка от 11-те системи.",
        "Check the insulin load, insulin index and nutritional balance of a meal BEFORE you eat it. Connected to the digital-twin network — you see the effect on each of the 11 systems.",
        "Compruebe la carga insulínica, el índice insulínico y el equilibrio nutricional de un plato ANTES de comerlo. Conectado a la red de gemelos digitales — vea el efecto en cada uno de los 11 sistemas."
      ),
      link: "/platform",
    },
    {
      icon: Activity,
      title: pickLang("Инсулинов товар и индекс", "Insulin load and index", "Carga e índice insulínicos"),
      goal: pickLang("Цел: отвъд калориите", "Goal: beyond calories", "Objetivo: más allá de las calorías"),
      body: pickLang(
        "Проследявате инсулиновия товар на храната и индивидуалния инсулинов индекс на всяко ястие. Калорийният дефицит не Ви пази от метаболитен синдром — инсулиновият баланс е ключът.",
        "Track the insulin load of your food and the individual insulin index of each meal. A calorie deficit doesn't protect you from metabolic syndrome — insulin balance is the key.",
        "Siga la carga insulínica de la comida y el índice insulínico individual de cada plato. El déficit calórico no le protege del síndrome metabólico — la clave es el equilibrio insulínico."
      ),
      link: "/platform",
    },
    {
      icon: FlaskConical,
      title: pickLang("Анализ на кръвни изследвания", "Lab test analysis", "Análisis de pruebas de sangre"),
      goal: pickLang("Цел: разберете всеки показател", "Goal: understand every marker", "Objetivo: comprender cada marcador"),
      body: pickLang(
        "Качвате PDF от лабораторията — получавате персонализирана интерпретация на всеки маркер в контекста на цялото Ви тяло. Без ненужна тревога, без пропуснати сигнали.",
        "Upload a lab PDF — receive a personalised interpretation of every marker in the context of your whole body. No unnecessary worry, no missed signals.",
        "Suba el PDF del laboratorio — reciba una interpretación personalizada de cada marcador en el contexto de todo su cuerpo. Sin preocupaciones innecesarias ni señales perdidas."
      ),
      link: "/platform",
    },
    {
      icon: Brain,
      title: pickLang("Мрежа от дигитални близнаци — 11 системи", "Digital-twin network — 11 systems", "Red de gemelos digitales — 11 sistemas"),
      goal: pickLang("Цел: цялостна картина, не отделни органи", "Goal: a complete picture, not isolated organs", "Objetivo: imagen completa, no órganos aislados"),
      body: pickLang(
        "Нервна, ендокринна, сърдечно-съдова, дихателна, храносмилателна, екскреторна, имунна, покривна, опорно-двигателна, репродуктивна, лимфна — обединени със 110+ научни връзки.",
        "Nervous, endocrine, cardiovascular, respiratory, digestive, excretory, immune, integumentary, musculoskeletal, reproductive, lymphatic — joined by 110+ scientific links.",
        "Nervioso, endocrino, cardiovascular, respiratorio, digestivo, excretor, inmunitario, tegumentario, musculoesquelético, reproductor, linfático — unidos por más de 110 enlaces científicos."
      ),
      link: "/platform",
    },
    {
      icon: Scan,
      title: pickLang("Скрининг инструменти", "Screening tools", "Herramientas de cribado"),
      goal: pickLang("Цел: ранно откриване от удобството на дома", "Goal: early detection from the comfort of home", "Objetivo: detección temprana desde casa"),
      body: pickLang(
        "Фото скенер за кожа (бенки, лезии), стоматологичен скрининг, Програма за здраве на гърдата (29 модула) — всеки с ясна препоръка за ниво на спешност.",
        "Photo scanner for skin (moles, lesions), dental screening, Breast Health Programme (29 modules) — each with a clear urgency recommendation.",
        "Escáner fotográfico para la piel (lunares, lesiones), cribado dental, Programa de salud mamaria (29 módulos) — cada uno con una recomendación de urgencia clara."
      ),
      link: "/tools",
    },
  ];
  return (
    <section
      id="features"
      data-testid="landing-v2-features"
      className={`py-20 sm:py-24 ${isDark ? "bg-[hsl(223,47%,8%)]" : "bg-white"}`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div
            className="inline-block text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: "#9c39a1" }}
          >
            {T.eyebrow}
          </div>
          <h2
            className={`text-3xl sm:text-4xl font-bold tracking-tight mb-4 ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            {T.title}
          </h2>
          <p
            className={`text-base ${
              isDark ? "text-slate-400" : "text-slate-600"
            }`}
          >
            {T.lead}
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <Link
                to={f.link}
                key={i}
                data-testid={`landing-v2-feature-${i}`}
                className={`group p-7 rounded-2xl transition-all hover:-translate-y-1 flex flex-col ${
                  isDark
                    ? "bg-[hsl(223,47%,10%)] border border-white/10 hover:border-white/20"
                    : "bg-[#F9F9F7] border border-slate-100 hover:border-slate-200 hover:shadow-md"
                }`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background:
                        "linear-gradient(135deg, #5e227f 0%, #e44288 100%)",
                    }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`text-lg font-semibold leading-tight flex items-center gap-2 ${
                        isDark ? "text-white" : "text-slate-900"
                      }`}
                    >
                      {f.title}
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </h3>
                  </div>
                </div>
                <div
                  className="inline-flex self-start text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md mb-3"
                  style={{
                    color: "#287a2e",
                    background: "rgba(40,122,46,0.08)",
                  }}
                >
                  {f.goal}
                </div>
                <p
                  className={`text-sm leading-relaxed ${
                    isDark ? "text-slate-400" : "text-slate-600"
                  }`}
                >
                  {f.body}
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Breast Health Spotlight — флагманска програма
// ─────────────────────────────────────────────────────────────────────
function BreastHealthSpotlight({ isDark }) {
  const { pickLang } = useLanguage();
  const T = {
    badge: pickLang("Флагманска програма", "Flagship programme", "Programa estrella"),
    titlePre: pickLang("Програма за", "Programme for", "Programa de"),
    titleAccent: pickLang("здраве на гърдата", "breast health", "salud mamaria"),
    body: pickLang(
      "Разработена от д-р Иван Инков — гръден хирург и онкомамолог с две десетилетия клиничен опит. Пациентът идва на преглед с пълна картина; лекарят вижда структурирана справка. Не замества медицинска консултация — улеснява я.",
      "Developed by Dr Ivan Inkov — a breast surgeon and oncomammologist with two decades of clinical practice. The patient comes to the appointment with a complete picture; the doctor sees a structured summary. It doesn't replace a medical consultation — it supports it.",
      "Desarrollado por el Dr. Ivan Inkov — cirujano de mama y oncomamólogo con dos décadas de experiencia clínica. El paciente llega a la consulta con una imagen completa; el médico ve un informe estructurado. No sustituye la consulta médica — la facilita."
    ),
    ctaPrimary: pickLang("Отворете програмата", "Open the programme", "Abrir el programa"),
    ctaSecondary: pickLang("За лекари", "For doctors", "Para médicos"),
    reportTitle: pickLang("Пример от доклада", "Report preview", "Vista previa del informe"),
    rowMeta: pickLang("Метаболитен скор", "Metabolic score", "Puntuación metabólica"),
    rowHered: pickLang("Наследствен риск", "Hereditary risk", "Riesgo hereditario"),
    rowDensity: pickLang("Плътност (BI-RADS)", "Density (BI-RADS)", "Densidad (BI-RADS)"),
    rowBioAge: pickLang("Биологична възраст", "Biological age", "Edad biológica"),
    moderate: pickLang("Умерен", "Moderate", "Moderado"),
    bioAgeVal: pickLang("−2 г.", "−2 yrs", "−2 años"),
    disclaimer: pickLang(
      "* Данните са илюстративни. Реалните стойности се персонализират за всеки потребител.",
      "* Illustrative data. Real values are personalised for every user.",
      "* Datos ilustrativos. Los valores reales se personalizan para cada usuario."
    ),
  };
  const highlights = [
    pickLang("Метаболитен скор на гърдата (0–100) с 5 под-измерения", "Breast metabolic score (0–100) with 5 sub-dimensions", "Puntuación metabólica mamaria (0–100) con 5 subdimensiones"),
    pickLang("Наследствен риск — генетика и фамилна история", "Hereditary risk — genetics and family history", "Riesgo hereditario — genética e historial familiar"),
    pickLang("Биологична възраст на тъканта и плътност (BI-RADS)", "Tissue biological age and density (BI-RADS)", "Edad biológica del tejido y densidad (BI-RADS)"),
    pickLang("Персонален план за превенция, базиран на Вашия профил", "Personal prevention plan based on your profile", "Plan de prevención personal basado en su perfil"),
    pickLang("AI обобщение на разбираем език + клиничен PDF за лекаря", "AI summary in plain language + clinical PDF for your doctor", "Resumen con IA en lenguaje claro + PDF clínico para su médico"),
    pickLang("Повече от 29 модулни секции в една програма", "Over 29 modular sections in one programme", "Más de 29 secciones modulares en un solo programa"),
  ];
  return (
    <section
      data-testid="landing-v2-breast-spotlight"
      className={`py-20 sm:py-24 relative overflow-hidden ${
        isDark ? "bg-[hsl(223,47%,7%)]" : "bg-white"
      }`}
    >
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #e44288 0%, #9c39a1 50%, transparent 70%)",
        }}
      />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5"
              style={{
                background: "rgba(228,66,136,0.1)",
                color: "#e44288",
                border: "1px solid rgba(228,66,136,0.2)",
              }}
              data-testid="landing-v2-breast-badge"
            >
              <HeartPulse className="w-3.5 h-3.5" />
              {T.badge}
            </div>
            <h2
              className={`text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-5 ${
                isDark ? "text-white" : "text-slate-900"
              }`}
            >
              {T.titlePre}{" "}
              <span
                style={{
                  background:
                    "linear-gradient(135deg, #5e227f 0%, #e44288 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {T.titleAccent}
              </span>
            </h2>
            <p
              className={`text-lg leading-relaxed mb-6 ${
                isDark ? "text-slate-300" : "text-slate-600"
              }`}
            >
              {T.body}
            </p>
            <ul className="space-y-2.5 mb-7">
              {highlights.map((h, i) => (
                <li
                  key={i}
                  data-testid={`landing-v2-breast-feature-${i}`}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    style={{ color: "#e44288" }}
                  />
                  <span
                    className={`text-sm sm:text-base ${
                      isDark ? "text-slate-300" : "text-slate-700"
                    }`}
                  >
                    {h}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/health-intelligence/breast">
                <Button
                  size="lg"
                  data-testid="landing-v2-breast-cta-primary"
                  className="brand-cta-primary rounded-full px-7 py-6 text-base font-semibold w-full sm:w-auto"
                >
                  {T.ctaPrimary}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/for-doctors">
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="landing-v2-breast-cta-secondary"
                  className={`rounded-full px-7 py-6 text-base font-semibold w-full sm:w-auto ${
                    isDark
                      ? "border-white/20 text-white hover:bg-white/5"
                      : "border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {T.ctaSecondary}
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div
              className={`rounded-3xl p-6 shadow-2xl ${
                isDark
                  ? "bg-[hsl(223,47%,10%)] border border-white/10"
                  : "bg-white border border-slate-200"
              }`}
              data-testid="landing-v2-breast-mock"
            >
              <div
                className="text-[11px] font-bold uppercase tracking-wider mb-4"
                style={{ color: "#e44288" }}
              >
                {T.reportTitle}
              </div>
              <div className="space-y-3">
                {[
                  { label: T.rowMeta, value: "72 / 100", tone: "#287a2e" },
                  { label: T.rowHered, value: T.moderate, tone: "#287a2e" },
                  { label: T.rowDensity, value: "C", tone: "#287a2e" },
                  { label: T.rowBioAge, value: T.bioAgeVal, tone: "#287a2e" },
                ].map((row) => (
                  <div
                    key={row.label}
                    className={`flex items-center justify-between py-2 border-b ${
                      isDark ? "border-white/5" : "border-slate-100"
                    }`}
                  >
                    <span
                      className={`text-sm ${
                        isDark ? "text-slate-400" : "text-slate-600"
                      }`}
                    >
                      {row.label}
                    </span>
                    <span
                      className="text-sm font-bold"
                      style={{ color: row.tone }}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
                <div
                  className={`mt-3 pt-3 text-xs ${
                    isDark ? "text-slate-500" : "text-slate-500"
                  }`}
                >
                  {T.disclaimer}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 6) Evidence + Testimonials
// ─────────────────────────────────────────────────────────────────────
function Evidence({ isDark }) {
  const { pickLang } = useLanguage();
  const T = {
    eyebrow: pickLang("Доказана наука", "Proven science", "Ciencia probada"),
    title: pickLang("Построено върху рецензирани научни изследвания", "Built on peer-reviewed research", "Basado en investigación revisada por pares"),
    body: pickLang(
      "Използваме данни от PubMed, Semantic Scholar, USDA, DrugBank и OpenFDA. Всяка препоръка е с източник — не измисляме, проверяваме.",
      "We use data from PubMed, Semantic Scholar, USDA, DrugBank and OpenFDA. Every recommendation has a source — we don't invent, we verify.",
      "Usamos datos de PubMed, Semantic Scholar, USDA, DrugBank y OpenFDA. Cada recomendación tiene su fuente — no inventamos, verificamos."
    ),
    link: pickLang("Вижте научната база", "See the science", "Ver la base científica"),
    statPubmed: pickLang("научни референции", "scientific references", "referencias científicas"),
    statSystems: pickLang("физиологични системи", "physiological systems", "sistemas fisiológicos"),
    statLinks: pickLang("научни връзки", "scientific links", "enlaces científicos"),
  };
  const testimonials = [
    {
      name: pickLang("Мария, 34 г.", "Maria, 34", "María, 34"),
      quote: pickLang(
        "За първи път разбирам защо след определени храни се чувствам замаяна. Промених три неща в диетата си — лабораторните показатели се подобриха за 2 месеца.",
        "For the first time I understand why I feel dizzy after certain foods. I changed three things in my diet — my lab values improved in 2 months.",
        "Por primera vez entiendo por qué me siento mareada tras ciertos alimentos. Cambié tres cosas en mi dieta — mis análisis mejoraron en 2 meses."
      ),
    },
    {
      name: pickLang("Иван, 48 г.", "Ivan, 48", "Iván, 48"),
      quote: pickLang(
        "Използвам платформата заедно с личния си лекар. Интерпретацията на изследванията ми спести много объркване и ненужни тревоги.",
        "I use the platform together with my doctor. The lab interpretation saved me a lot of confusion and unnecessary worry.",
        "Uso la plataforma junto con mi médico. La interpretación de los análisis me ahorró mucha confusión y preocupaciones innecesarias."
      ),
    },
    {
      name: pickLang("Елена, 29 г.", "Elena, 29", "Elena, 29"),
      quote: pickLang(
        "Снимам ястията си преди да ги изям. За 30 дни намалих възпалителните маркери с видим ефект върху кожата ми.",
        "I photograph my meals before eating them. In 30 days I lowered my inflammation markers, with a visible effect on my skin.",
        "Fotografío mis comidas antes de comer. En 30 días reduje mis marcadores inflamatorios, con un efecto visible en mi piel."
      ),
    },
  ];
  return (
    <section
      data-testid="landing-v2-evidence"
      className={`py-20 sm:py-24 ${
        isDark ? "bg-[hsl(223,47%,7%)]" : "bg-[#F9F9F7]"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-10 mb-14 items-center">
          <div className="lg:col-span-1">
            <div
              className="inline-block text-xs font-semibold uppercase tracking-wider mb-3"
              style={{ color: "#287a2e" }}
            >
              {T.eyebrow}
            </div>
            <h2
              className={`text-3xl sm:text-4xl font-bold tracking-tight mb-4 ${
                isDark ? "text-white" : "text-slate-900"
              }`}
            >
              {T.title}
            </h2>
            <p
              className={`text-base mb-5 ${
                isDark ? "text-slate-400" : "text-slate-600"
              }`}
            >
              {T.body}
            </p>
            <Link
              to="/scientific-formulas"
              data-testid="landing-v2-evidence-link"
              className="inline-flex items-center gap-1 text-sm font-semibold"
              style={{ color: "#287a2e" }}
            >
              {T.link} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-3 gap-4">
            {[
              { n: "PubMed", l: T.statPubmed },
              { n: "11", l: T.statSystems },
              { n: "110+", l: T.statLinks },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`landing-v2-stat-${i}`}
                className={`p-6 rounded-2xl text-center ${
                  isDark
                    ? "bg-[hsl(223,47%,10%)] border border-white/10"
                    : "bg-white border border-slate-100"
                }`}
              >
                <div
                  className="text-3xl sm:text-4xl font-black mb-1"
                  style={{
                    background:
                      "linear-gradient(135deg, #5e227f 0%, #e44288 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {s.n}
                </div>
                <div
                  className={`text-sm ${
                    isDark ? "text-slate-400" : "text-slate-600"
                  }`}
                >
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={i}
              data-testid={`landing-v2-testimonial-${i}`}
              className={`p-6 rounded-2xl ${
                isDark
                  ? "bg-[hsl(223,47%,10%)] border border-white/10"
                  : "bg-white border border-slate-100"
              }`}
            >
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((j) => (
                  <Star
                    key={j}
                    className="w-4 h-4 fill-amber-400 text-amber-400"
                  />
                ))}
              </div>
              <p
                className={`text-sm leading-relaxed mb-4 ${
                  isDark ? "text-slate-300" : "text-slate-700"
                }`}
              >
                „{t.quote}"
              </p>
              <div
                className={`text-xs font-semibold ${
                  isDark ? "text-slate-400" : "text-slate-500"
                }`}
              >
                — {t.name}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 7) Pricing + Final CTA
// ─────────────────────────────────────────────────────────────────────
function Pricing({ isDark }) {
  const { pickLang } = useLanguage();
  const T = {
    eyebrow: pickLang("Цени", "Pricing", "Precios"),
    title: pickLang("Започнете безплатно. Надстройте, когато сте готови.", "Start free. Upgrade when you're ready.", "Empiece gratis. Pase a premium cuando esté listo."),
    lead: pickLang("Без скрити такси. Отменете по всяко време.", "No hidden fees. Cancel any time.", "Sin tarifas ocultas. Cancele cuando quiera."),
    forever: pickLang("€ завинаги", "€ forever", "€ para siempre"),
    perMonth: pickLang("€ / месец", "€ / month", "€ / mes"),
    popular: pickLang("Най-популярен", "Most popular", "Más popular"),
    anchorTitle: pickLang("Стандартна цена за ЕС", "Standard EU price", "Precio estándar UE"),
  };
  const plans = [
    {
      name: pickLang("Безплатен", "Free", "Gratis"),
      price: "0",
      period: T.forever,
      tagline: pickLang("За да опитате спокойно", "So you can try with peace of mind", "Para que pruebe con tranquilidad"),
      highlight: false,
      features: [
        pickLang("До 5 анализа на хранене дневно", "Up to 5 meal analyses per day", "Hasta 5 análisis de comidas al día"),
        pickLang("Дневник с инсулинов товар", "Insulin-load food diary", "Diario con carga insulínica"),
        pickLang("Скриниращи въпросници за риск", "Risk screening questionnaires", "Cuestionarios de cribado de riesgo"),
        pickLang("Образователна библиотека", "Educational library", "Biblioteca educativa"),
      ],
      cta: pickLang("Започнете безплатно", "Start free", "Empezar gratis"),
      ctaLink: "/register",
      testid: "landing-v2-plan-free",
    },
    {
      name: "Pro",
      price: "19.90",
      anchor: "24.90",
      period: T.perMonth,
      tagline: pickLang("Пълна метаболитна картина", "Full metabolic picture", "Imagen metabólica completa"),
      highlight: true,
      features: [
        pickLang("Неограничени анализи на хранене по снимка", "Unlimited photo-based meal analyses", "Análisis ilimitados de comidas por foto"),
        pickLang("Автоматично разчитане на кръвни изследвания", "Automatic lab result interpretation", "Interpretación automática de análisis"),
        pickLang("Висцерален мастен индекс и план за намаляване", "Visceral fat index and reduction plan", "Índice de grasa visceral y plan de reducción"),
        pickLang("Мрежата от дигитални близнаци на органите", "Digital-twin organ network", "Red de gemelos digitales de los órganos"),
        pickLang("Програма за здраве на гърдата", "Breast Health Programme", "Programa de salud mamaria"),
        pickLang("Експорт на отчет за Вашия лекар", "Report export for your doctor", "Exportación de informe para su médico"),
      ],
      cta: pickLang("Опитайте 30 дни безплатно", "Try 30 days free", "Pruebe 30 días gratis"),
      ctaLink: "/pricing",
      testid: "landing-v2-plan-pro",
    },
    {
      name: pickLang("Клиника", "Clinic", "Clínica"),
      price: "49",
      period: T.perMonth,
      tagline: pickLang("За специалисти и практики", "For specialists and practices", "Para especialistas y consultas"),
      highlight: false,
      features: [
        pickLang("Всичко от Pro, плюс екипен достъп", "Everything in Pro, plus team access", "Todo lo de Pro, más acceso para el equipo"),
        pickLang("Пациентски профили и проследяване", "Patient profiles and follow-up", "Perfiles de pacientes y seguimiento"),
        pickLang("Готови клинични бележки и отчети", "Ready-made clinical notes and reports", "Notas e informes clínicos listos"),
        pickLang("Модули само за медицински специалисти", "Modules only for medical professionals", "Módulos exclusivos para profesionales médicos"),
      ],
      cta: pickLang("Вижте плановете за клиники", "See clinic plans", "Ver planes para clínicas"),
      ctaLink: "/pricing",
      testid: "landing-v2-plan-clinic",
    },
  ];
  return (
    <section
      id="pricing"
      data-testid="landing-v2-pricing"
      className={`py-20 sm:py-24 ${isDark ? "bg-[hsl(223,47%,8%)]" : "bg-white"}`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div
            className="inline-block text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: "#9c39a1" }}
          >
            {T.eyebrow}
          </div>
          <h2
            className={`text-3xl sm:text-4xl font-bold tracking-tight mb-4 ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            {T.title}
          </h2>
          <p className={`text-base ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {T.lead}
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((p, i) => (
            <div
              key={i}
              data-testid={p.testid}
              className={`relative p-7 rounded-3xl flex flex-col ${
                p.highlight
                  ? "border-2 md:-mt-3 md:mb-[-0.75rem] shadow-xl"
                  : isDark
                  ? "bg-[hsl(223,47%,10%)] border border-white/10"
                  : "bg-[#F9F9F7] border border-slate-100"
              }`}
              style={
                p.highlight
                  ? {
                      background: isDark
                        ? "linear-gradient(135deg, rgba(94,34,127,0.15), rgba(228,66,136,0.1))"
                        : "linear-gradient(135deg, rgba(94,34,127,0.03), rgba(228,66,136,0.03))",
                      borderColor: "#e44288",
                    }
                  : {}
              }
            >
              {p.highlight && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white"
                  style={{
                    background:
                      "linear-gradient(135deg, #5e227f 0%, #e44288 100%)",
                  }}
                >
                  {T.popular}
                </div>
              )}
              <h3
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-slate-900"
                }`}
              >
                {p.name}
              </h3>
              <p className={`text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {p.tagline}
              </p>
              <div className="flex items-baseline gap-1.5 mb-6">
                {p.anchor && (
                  <span
                    className={`text-sm line-through ${isDark ? "text-slate-600" : "text-slate-400"}`}
                    title={T.anchorTitle}
                  >
                    {p.anchor}
                  </span>
                )}
                <span
                  className={`text-4xl font-black tracking-tight ${
                    isDark ? "text-white" : "text-slate-900"
                  }`}
                >
                  {p.price}
                </span>
                <span
                  className={`text-sm ${
                    isDark ? "text-slate-400" : "text-slate-500"
                  }`}
                >
                  {p.period}
                </span>
              </div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <CheckCircle2
                      className="w-4 h-4 flex-shrink-0 mt-0.5"
                      style={{ color: "#287a2e" }}
                    />
                    <span
                      className={`text-sm leading-snug ${
                        isDark ? "text-slate-300" : "text-slate-700"
                      }`}
                    >
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
              <Link to={p.ctaLink} className="mt-auto">
                <Button
                  size="lg"
                  data-testid={`${p.testid}-cta`}
                  className={`w-full rounded-full font-semibold ${
                    p.highlight
                      ? "brand-cta-primary"
                      : isDark
                      ? "bg-white/10 hover:bg-white/15 text-white"
                      : "bg-slate-900 hover:bg-slate-800 text-white"
                  }`}
                >
                  {p.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 7) Създателят на платформата — сдържана визитка
// ─────────────────────────────────────────────────────────────────────
function Founder({ isDark }) {
  const { pickLang } = useLanguage();
  const t = {
    eyebrow: pickLang(
      "За създателя",
      "About the author",
      "Sobre el autor"
    ),
    name: pickLang("Д-р Иван Инков", "Dr Ivan Inkov", "Dr. Ivan Inkov"),
    role: pickLang(
      "Гръден хирург и онкомамолог",
      "Breast surgeon and oncomammologist",
      "Cirujano de mama y oncomamólogo"
    ),
    intro: pickLang(
      "Платформата започна като инструмент за собствената му практика — тих начин да покаже на пациент защо тялото реагира точно така.",
      "The platform began as a tool for his own practice — a quiet way to show a patient why the body responds the way it does.",
      "La plataforma comenzó como una herramienta para su propia práctica — una manera discreta de mostrar al paciente por qué el cuerpo responde así."
    ),
    quote: pickLang(
      "Идеята възникна в кабинета — да свържа процесите, които наблюдавам при пациентите си, в една обща картина.",
      "The idea came up in the consulting room — to connect the processes I follow in my own patients into one picture.",
      "La idea surgió en la consulta — unir los procesos que sigo con mis pacientes en una sola imagen."
    ),
    cta: pickLang(
      "Повече за автора",
      "More about the author",
      "Más sobre el autor"
    ),
  };

  const divider = isDark ? "bg-white/15" : "bg-slate-300";
  const bg = isDark ? "bg-[hsl(223,47%,6%)]" : "bg-[#FAFAF7]";
  const border = isDark ? "border-white/5" : "border-slate-200/70";
  const eyebrowCol = isDark ? "text-slate-400" : "text-slate-500";
  const nameCol = isDark ? "text-white" : "text-slate-900";
  const roleCol = isDark ? "text-slate-400" : "text-slate-500";
  const introCol = isDark ? "text-slate-300" : "text-slate-700";
  const quoteCol = isDark ? "text-slate-400" : "text-slate-500";
  const linkCol = isDark
    ? "text-slate-300 hover:text-white"
    : "text-slate-700 hover:text-slate-900";

  return (
    <section
      id="founder"
      data-testid="landing-v2-founder"
      className={`py-20 sm:py-24 border-t ${bg} ${border}`}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row gap-8 sm:gap-10 items-start">
          <img
            src={DR_INKOV_PHOTO}
            alt={t.name}
            loading="lazy"
            data-testid="landing-v2-founder-photo"
            className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover shrink-0 grayscale opacity-95"
          />
          <div className="min-w-0 flex-1">
            <div
              className={`text-[11px] font-medium uppercase tracking-[0.22em] ${eyebrowCol}`}
            >
              {t.eyebrow}
            </div>
            <h2
              className={`mt-2 text-xl sm:text-2xl font-medium tracking-tight ${nameCol}`}
              style={{ fontFamily: "'Work Sans', 'IBM Plex Sans', sans-serif" }}
              data-testid="landing-v2-founder-name"
            >
              {t.name}
            </h2>
            <p className={`mt-0.5 text-sm ${roleCol}`}>{t.role}</p>

            <p className={`mt-5 text-[15px] leading-relaxed ${introCol}`}>
              {t.intro}
            </p>

            <p
              className={`mt-4 text-sm leading-relaxed italic ${quoteCol}`}
            >
              „{t.quote}"
            </p>

            <div className={`mt-6 h-px w-10 ${divider}`} aria-hidden="true" />

            <Link
              to="/consultants/inkov"
              data-testid="landing-v2-founder-cta"
              className={`inline-flex items-center gap-1.5 mt-5 text-sm ${linkCol} transition-colors`}
            >
              {t.cta}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Compact Footer
// ─────────────────────────────────────────────────────────────────────
function FooterV2({ isDark }) {
  const { pickLang } = useLanguage();
  const T = {
    tagline: pickLang(
      "Образователна платформа за персонализирано метаболитно здраве. Не заменя медицинска консултация.",
      "Educational platform for personalised metabolic health. Does not replace medical consultation.",
      "Plataforma educativa de salud metabólica personalizada. No sustituye la consulta médica."
    ),
    colPlatform: pickLang("Платформа", "Platform", "Plataforma"),
    colKnowledge: pickLang("Знание", "Knowledge", "Conocimiento"),
    colCompany: pickLang("Компания", "Company", "Empresa"),
    features: pickLang("Функции", "Features", "Funciones"),
    pricing: pickLang("Цени", "Pricing", "Precios"),
    tools: pickLang("Инструменти", "Tools", "Herramientas"),
    knowledgeBase: pickLang("База знания", "Knowledge base", "Base de conocimiento"),
    formulas: pickLang("Формули и методика", "Formulas and methodology", "Fórmulas y metodología"),
    doctors: pickLang("За лекари", "For doctors", "Para médicos"),
    about: pickLang("За нас", "About", "Sobre nosotros"),
    contact: pickLang("Контакти", "Contact", "Contacto"),
    legal: pickLang("Правни", "Legal", "Legal"),
    rights: pickLang("Всички права запазени.", "All rights reserved.", "Todos los derechos reservados."),
  };
  return (
    <footer
      data-testid="landing-v2-footer"
      className={`py-12 border-t ${
        isDark
          ? "bg-[hsl(223,47%,6%)] border-white/10"
          : "bg-[#F9F9F7] border-slate-100"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src="/app-icon.png" alt="Nutramilo" className="h-7 w-7" />
              <span
                className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}
              >
                Nutramilo
              </span>
            </div>
            <p
              className={`text-xs leading-relaxed ${
                isDark ? "text-slate-400" : "text-slate-500"
              }`}
            >
              {T.tagline}
            </p>
          </div>
          {[
            {
              h: T.colPlatform,
              links: [
                { l: T.features, to: "/platform" },
                { l: T.pricing, to: "/pricing" },
                { l: T.tools, to: "/tools" },
              ],
            },
            {
              h: T.colKnowledge,
              links: [
                { l: T.knowledgeBase, to: "/knowledge-library" },
                { l: T.formulas, to: "/scientific-formulas" },
                { l: T.doctors, to: "/for-doctors" },
              ],
            },
            {
              h: T.colCompany,
              links: [
                { l: T.about, to: "/about" },
                { l: T.contact, to: "/contact" },
                { l: T.legal, to: "/legal" },
              ],
            },
          ].map((col) => (
            <div key={col.h}>
              <h4
                className={`text-sm font-semibold mb-3 ${
                  isDark ? "text-white" : "text-slate-900"
                }`}
              >
                {col.h}
              </h4>
              <ul className="space-y-2">
                {col.links.map((l) => (
                  <li key={l.l}>
                    <Link
                      to={l.to}
                      className={`text-xs ${
                        isDark
                          ? "text-slate-400 hover:text-white"
                          : "text-slate-500 hover:text-slate-900"
                      }`}
                    >
                      {l.l}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div
          className={`pt-6 border-t text-xs ${
            isDark
              ? "border-white/10 text-slate-500"
              : "border-slate-200 text-slate-500"
          }`}
        >
          © {new Date().getFullYear()} Nutramilo. {T.rights}
        </div>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────────
export default function LandingPageV2() {
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuth();
  const { pickLang } = useLanguage();
  const seoTitle = pickLang(
    "Nutramilo — Разберете какво всяко хранене прави с тялото Ви",
    "Nutramilo — Understand what every meal does to your body",
    "Nutramilo — Comprenda qué hace cada comida en su cuerpo"
  );
  const seoDesc = pickLang(
    "Образователна платформа за персонализирано метаболитно здраве. Снимайте ястия, качвайте кръвни изследвания, получавайте научно обосновани препоръки.",
    "Educational platform for personalised metabolic health. Snap meals, upload lab tests, receive evidence-based recommendations.",
    "Plataforma educativa de salud metabólica personalizada. Fotografíe comidas, suba análisis y reciba recomendaciones basadas en evidencia."
  );
  return (
    <div
      data-testid="landing-v2-root"
      className={`min-h-screen overflow-x-hidden ${
        isDark ? "bg-[hsl(223,47%,7%)]" : "bg-white"
      }`}
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <SEO
        title={seoTitle}
        description={seoDesc}
      />
      <NavV2 isDark={isDark} toggleTheme={toggleTheme} user={user} />
      <Hero isDark={isDark} />
      <VideoShowcase isDark={isDark} />
      <Problem isDark={isDark} />
      <Solution isDark={isDark} />
      <Features isDark={isDark} />
      <BreastHealthSpotlight isDark={isDark} />
      <Evidence isDark={isDark} />
      <Founder isDark={isDark} />
      <Pricing isDark={isDark} />
      <FooterV2 isDark={isDark} />
    </div>
  );
}
